/* Iter85: all remaining design-gallery layouts extracted from
   SlidePreviewBody.jsx into a single dispatcher module. Each layout is
   its own render function; `renderGalleryLayout()` picks the matching
   one by prefix. The main SlidePreviewBody delegates all non-locked
   slides here, so its own body stays a thin ~200-line dispatcher.

   Shared UI conventions carried over verbatim (padding, colours, font
   sizes) — no visual changes. */

import React, { useState } from "react";
import PhotoTile from "@/components/PhotoTile";

/** Picture-only empty state — click OR drag-and-drop an image file. */
export function PictureOnlyDropzone({ primary, onUploadPhoto }) {
  const [isDragging, setIsDragging] = useState(false);

  const acceptFile = (file) => {
    if (!file) return false;
    return /^image\/(png|jpe?g|webp|gif)$/i.test(file.type);
  };
  const onDragOver = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };
  const onDragLeave = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (acceptFile(file)) onUploadPhoto(file);
  };

  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onUploadPhoto(); }}
      onDragEnter={onDragOver}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      data-testid="picture-only-upload-cta"
      className="relative h-full w-full flex flex-col items-center justify-center gap-3 group transition-all"
      style={{
        backgroundColor: isDragging ? `${primary}12` : "#F8FAFC",
        border: `2px dashed ${isDragging ? primary : `${primary}66`}`,
        cursor: "pointer",
        boxShadow: isDragging ? `inset 0 0 0 4px ${primary}22` : "none",
      }}
      title="Click or drag a photo here"
    >
      <div className="rounded-full flex items-center justify-center transition-transform group-hover:scale-110"
        style={{
          width: "clamp(56px, 8vw, 92px)",
          height: "clamp(56px, 8vw, 92px)",
          backgroundColor: `${primary}18`,
          border: `1.5px dashed ${primary}88`,
          color: primary,
          fontSize: "clamp(28px, 4vw, 44px)",
          fontWeight: 300,
          lineHeight: 1,
          transform: isDragging ? "scale(1.12)" : "none",
          transition: "transform 160ms ease-out",
        }}>+</div>
      <div className="text-center px-6">
        <div className="font-extrabold tracking-tight"
          style={{ color: "#0F172A", fontSize: "clamp(14px, 1.8vw, 20px)" }}>
          {isDragging ? "Drop to upload" : "Click or drag a photo here"}
        </div>
        <div className="mt-1" style={{ color: "#64748B", fontSize: "clamp(10px, 1.1vw, 13px)" }}>
          PNG · JPG · WEBP · GIF · This layout is just a picture, no text
        </div>
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// Layout renderers — one per gallery variant. Each function takes
// the shared `ctx` (layout, deck, slide, bullets, primary, Header,
// Footer, spacing, onUploadPhoto) and returns JSX.
// ─────────────────────────────────────────────────────────────

function renderFullBleedHero({ deck, slide, bullets, primary }) {
  return (
    <div className="h-full flex flex-col justify-center relative"
      style={{ background: primary, padding: "clamp(20px, 4vw, 40px)" }}>
      <div className="text-[10px] font-bold uppercase tracking-[0.24em] mb-2"
        style={{ color: "rgba(255,255,255,0.65)" }}>
        {(deck.industry || "investor pitch").replace("_", " ").toUpperCase()}
      </div>
      <div className="font-extrabold tracking-tight"
        style={{ color: "#fff", fontSize: "clamp(28px, 5.4vw, 60px)", letterSpacing: "-0.03em", lineHeight: 1.02 }}>
        {slide.title === "Cover & Snapshot" ? (deck.company_name || slide.title) : slide.title}
      </div>
      {(deck.tagline || bullets[0]) && (
        <div className="mt-3" style={{ color: "rgba(255,255,255,0.85)", fontSize: "clamp(13px, 1.8vw, 22px)" }}>
          {deck.tagline || bullets[0]}
        </div>
      )}
    </div>
  );
}

function renderKPIDashboard({ bullets, primary, Header, Footer }) {
  const cards = bullets.slice(0, 4);
  while (cards.length < 4) cards.push("—");
  return (
    <div className="relative h-full flex flex-col" style={{ padding: "clamp(16px, 3vw, 32px)" }}>
      {Header}
      <div className="mt-3 flex-1 grid grid-cols-4 gap-2">
        {cards.map((c, i) => {
          const parts = c.split(" ");
          const val = /\d/.test(parts[0] || "") ? parts[0] : "—";
          const lab = /\d/.test(parts[0] || "") ? parts.slice(1).join(" ") : c;
          return (
            <div key={i} className="rounded-md flex flex-col items-center justify-center relative"
              style={{ backgroundColor: `${primary}12`, padding: "clamp(4px, 1vw, 10px)" }}>
              <div className="absolute top-0 left-0 right-0" style={{ height: 2, backgroundColor: primary }} />
              <div className="font-extrabold" style={{ color: primary, fontSize: "clamp(18px, 3vw, 36px)", lineHeight: 1 }}>{val}</div>
              <div className="text-center leading-tight mt-1" style={{ color: "#1F2937", fontSize: "clamp(9px, 1.1vw, 12px)" }}>
                {lab.substring(0, 30)}
              </div>
            </div>
          );
        })}
      </div>
      {Footer}
    </div>
  );
}

function renderCardGrid({ layout, bullets, primary, Header, Footer }) {
  const cards = bullets.slice(0, layout.startsWith("Icon grid") ? 4 : 3);
  while (cards.length < 3) cards.push("Add a supporting point");
  const cols = cards.length === 4 ? "grid-cols-2" : "grid-cols-3";
  return (
    <div className="relative h-full flex flex-col" style={{ padding: "clamp(16px, 3vw, 32px)" }}>
      {Header}
      <div className={`mt-3 flex-1 grid ${cols} gap-2`}>
        {cards.map((c, i) => (
          <div key={i} className="rounded-md p-2 flex flex-col items-center justify-center text-center"
            style={{ backgroundColor: `${primary}10` }}>
            <div className="rounded-full mb-1.5 flex items-center justify-center font-bold text-white"
              style={{ width: 24, height: 24, backgroundColor: primary, fontSize: 11 }}>
              {String(i + 1).padStart(2, "0")}
            </div>
            <div style={{ color: "#1F2937", fontSize: "clamp(10px, 1.2vw, 13px)", fontWeight: 600, lineHeight: 1.25 }}>
              {c}
            </div>
          </div>
        ))}
      </div>
      {Footer}
    </div>
  );
}

function renderProcessFlow({ bullets, primary, Header, Footer }) {
  const steps = bullets.slice(0, 4);
  while (steps.length < 3) steps.push("Add a step");
  return (
    <div className="relative h-full flex flex-col" style={{ padding: "clamp(16px, 3vw, 32px)" }}>
      {Header}
      <div className="mt-3 flex-1 flex items-center gap-2">
        {steps.map((s, i) => (
          <div key={i} className="flex-1 flex items-center gap-1">
            <div className="flex-1 rounded-md p-2 flex items-center gap-2" style={{ backgroundColor: `${primary}12` }}>
              <div className="rounded-full font-bold text-white flex items-center justify-center"
                style={{ width: 22, height: 22, backgroundColor: primary, fontSize: 11 }}>
                {i + 1}
              </div>
              <div style={{ color: "#1F2937", fontSize: "clamp(10px, 1.1vw, 12px)", fontWeight: 600 }}>{s.substring(0, 24)}</div>
            </div>
            {i < steps.length - 1 && <span style={{ color: primary, fontSize: 18 }}>›</span>}
          </div>
        ))}
      </div>
      {Footer}
    </div>
  );
}

function renderCompetitiveMatrix({ bullets, primary, Header, Footer }) {
  return (
    <div className="relative h-full flex flex-col" style={{ padding: "clamp(16px, 3vw, 32px)" }}>
      {Header}
      <div className="mt-3 flex-1 relative border-2 rounded-md" style={{ borderColor: `${primary}55` }}>
        <div className="absolute left-1/2 top-0 bottom-0 border-l" style={{ borderColor: `${primary}55` }} />
        <div className="absolute top-1/2 left-0 right-0 border-t" style={{ borderColor: `${primary}55` }} />
        <div className="absolute right-2 top-2 text-[10px] font-bold" style={{ color: primary }}>Leader</div>
        <div className="absolute right-4 top-4">
          <div className="rounded-full flex items-center justify-center font-bold text-white"
            style={{ width: 28, height: 28, backgroundColor: "#F59E0B", fontSize: 10, boxShadow: "0 0 0 3px rgba(245,158,11,0.25)" }}>
            You
          </div>
        </div>
        {bullets.slice(0, 3).map((c, i) => (
          <div key={i} className="absolute" style={{ top: `${30 + i * 15}%`, left: `${15 + i * 10}%` }}>
            <div className="rounded-full" style={{ width: 10, height: 10, backgroundColor: `${primary}88` }} />
            <div className="text-[9px] mt-0.5" style={{ color: "#6B7280" }}>{c.substring(0, 12)}</div>
          </div>
        ))}
      </div>
      {Footer}
    </div>
  );
}

function renderTimeline({ bullets, primary, Header, Footer }) {
  const points = bullets.slice(0, 5);
  while (points.length < 3) points.push("Add a milestone");
  return (
    <div className="relative h-full flex flex-col" style={{ padding: "clamp(16px, 3vw, 32px)" }}>
      {Header}
      <div className="mt-3 flex-1 flex items-center">
        <div className="w-full relative">
          <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2" style={{ height: 2, backgroundColor: `${primary}55` }} />
          <div className="relative flex justify-between items-center">
            {points.map((p, i) => (
              <div key={i} className="flex flex-col items-center text-center" style={{ width: `${100 / points.length}%` }}>
                <div className="rounded-full font-bold text-white flex items-center justify-center relative z-10"
                  style={{ width: 26, height: 26, backgroundColor: primary, fontSize: 11, border: "3px solid #fff" }}>
                  {i + 1}
                </div>
                <div className="mt-2 px-1" style={{ color: "#1F2937", fontSize: "clamp(10px, 1.15vw, 12px)", fontWeight: 600, lineHeight: 1.25 }}>
                  {p.substring(0, 32)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {Footer}
    </div>
  );
}

function renderFunnel({ bullets, primary, Header, Footer }) {
  const stages = bullets.slice(0, 5);
  while (stages.length < 3) stages.push("Add a stage");
  return (
    <div className="relative h-full flex flex-col" style={{ padding: "clamp(16px, 3vw, 32px)" }}>
      {Header}
      <div className="mt-3 flex-1 flex flex-col items-center justify-center gap-1.5">
        {stages.map((s, i) => (
          <div key={i} className="rounded-md flex items-center justify-center text-center px-3 py-1"
            style={{
              width: `${100 - i * (60 / stages.length)}%`,
              backgroundColor: `${primary}${["44", "33", "22", "18", "12"][i] || "12"}`,
              color: "#1F2937", fontSize: "clamp(10px, 1.2vw, 13px)", fontWeight: 600,
            }}>
            {s.substring(0, 28)}
          </div>
        ))}
      </div>
      {Footer}
    </div>
  );
}

function renderIllustratedHeroSplit({ slide, bullets, primary, Header, Footer, spacing }) {
  const photo = (slide?.images || [])[0];
  return (
    <div className="relative h-full grid grid-cols-2 gap-0" style={{ padding: 0 }}>
      <div className="flex flex-col" style={{ padding: "clamp(16px, 3vw, 32px)" }}>
        {Header}
        <div className={`mt-3 flex-1 flex flex-col ${spacing.justify} ${spacing.gap}`}>
          {bullets.slice(0, 5).map((b, i) => (
            <div key={i} className="flex items-start gap-1.5" style={{ color: "#1F2937", fontSize: "clamp(10px, 1.3vw, 14px)" }}>
              <span className="inline-block mt-1.5 shrink-0" style={{ width: 5, height: 5, backgroundColor: primary }} />
              <span className="leading-snug">{b}</span>
            </div>
          ))}
        </div>
        {Footer}
      </div>
      <div className="relative overflow-hidden" style={{ backgroundColor: `${primary}0d` }}>
        <PhotoTile photo={photo} primary={primary} />
      </div>
    </div>
  );
}

function renderPhotoLeft({ slide, bullets, primary, Header, Footer, spacing }) {
  const photo = (slide?.images || [])[0];
  return (
    <div className="relative h-full grid grid-cols-2 gap-0" style={{ padding: 0 }}>
      <div className="relative overflow-hidden" style={{ backgroundColor: `${primary}0d` }}>
        <PhotoTile photo={photo} primary={primary} />
      </div>
      <div className="flex flex-col" style={{ padding: "clamp(16px, 3vw, 32px)" }}>
        {Header}
        <div className={`mt-3 flex-1 flex flex-col ${spacing.justify} ${spacing.gap}`}>
          {bullets.slice(0, 5).map((b, i) => (
            <div key={i} className="flex items-start gap-1.5" style={{ color: "#1F2937", fontSize: "clamp(10px, 1.3vw, 14px)" }}>
              <span className="inline-block mt-1.5 shrink-0" style={{ width: 5, height: 5, backgroundColor: primary }} />
              <span className="leading-snug">{b}</span>
            </div>
          ))}
        </div>
        {Footer}
      </div>
    </div>
  );
}

function renderPhotoTop({ slide, bullets, primary, Header, Footer, spacing }) {
  const photo = (slide?.images || [])[0];
  return (
    <div className="relative h-full flex flex-col" style={{ padding: 0 }}>
      <div className="relative overflow-hidden" style={{ flex: "0 0 46%", backgroundColor: `${primary}0d` }}>
        <PhotoTile photo={photo} primary={primary} iconSize={42} sublabel="" />
      </div>
      <div className="flex flex-col flex-1" style={{ padding: "clamp(14px, 2.6vw, 26px)" }}>
        {Header}
        <div className={`mt-2 flex-1 flex flex-col ${spacing.justify} ${spacing.gap}`}>
          {bullets.slice(0, 4).map((b, i) => (
            <div key={i} className="flex items-start gap-1.5" style={{ color: "#1F2937", fontSize: "clamp(10px, 1.25vw, 13px)" }}>
              <span className="inline-block mt-1.5 shrink-0" style={{ width: 5, height: 5, backgroundColor: primary }} />
              <span className="leading-snug">{b}</span>
            </div>
          ))}
        </div>
        {Footer}
      </div>
    </div>
  );
}

function renderPhotoHero({ deck, slide, bullets, primary, spacing }) {
  const photo = (slide?.images || [])[0];
  return (
    <div className="relative h-full overflow-hidden" style={{ padding: 0, backgroundColor: `${primary}22` }}>
      <PhotoTile
        photo={photo}
        primary={primary}
        iconSize={52}
        sublabel="It fills the full slide behind the text panel."
      />
      <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(10,20,25,0.55) 0%, rgba(10,20,25,0.35) 55%, rgba(10,20,25,0.2) 100%)" }} />
      <div className="relative h-full flex flex-col" style={{ padding: "clamp(16px, 3vw, 32px)", width: "62%" }}>
        <div className="absolute top-0 left-0 right-0" style={{ height: 4, backgroundColor: primary }} />
        <div className="flex items-start justify-between">
          <div className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: "#fff", opacity: 0.85 }}>
            / {String(slide.num).padStart(2, "0")}{slide.pillar ? ` · ${slide.pillar}` : ""}
          </div>
        </div>
        <div className="mt-2 font-extrabold tracking-tight"
          style={{ color: "#fff", fontSize: "clamp(22px, 4vw, 40px)", letterSpacing: "-0.02em", lineHeight: 1.15, textShadow: "0 1px 3px rgba(0,0,0,0.4)" }}>
          {slide.title}
        </div>
        {/* Iter84: sub-question removed. */}
        <div className={`mt-3 flex-1 flex flex-col ${spacing.justify} ${spacing.gap} rounded-lg`} style={{ backgroundColor: "rgba(10,20,25,0.55)", padding: "clamp(10px, 1.6vw, 16px)", backdropFilter: "blur(4px)" }}>
          {bullets.slice(0, 5).map((b, i) => (
            <div key={i} className="flex items-start gap-1.5" style={{ color: "#fff", fontSize: "clamp(10px, 1.3vw, 14px)" }}>
              <span className="inline-block mt-1.5 shrink-0 rounded-full" style={{ width: 5, height: 5, backgroundColor: "#F59E0B" }} />
              <span className="leading-snug">{b}</span>
            </div>
          ))}
        </div>
        <div className="pt-2 mt-2 flex items-center justify-between text-[9px]"
          style={{ color: "rgba(255,255,255,0.75)", borderTop: "1px solid rgba(255,255,255,0.2)" }}>
          <span className="uppercase tracking-widest">{(deck.company_name || "").toUpperCase()}</span>
          <span className="font-mono">{String(slide.num).padStart(2, "0")} / 12</span>
        </div>
      </div>
    </div>
  );
}

function renderPhotoGrid({ slide, bullets, primary, Header, Footer, spacing }) {
  const photos = (slide?.images || []).slice(0, 3);
  return (
    <div className="relative h-full grid gap-0" style={{ padding: 0, gridTemplateColumns: "1.15fr 1fr" }}>
      <div className="flex flex-col" style={{ padding: "clamp(16px, 3vw, 32px)" }}>
        {Header}
        <div className={`mt-3 flex-1 flex flex-col ${spacing.justify} ${spacing.gap}`}>
          {bullets.slice(0, 5).map((b, i) => (
            <div key={i} className="flex items-start gap-1.5" style={{ color: "#1F2937", fontSize: "clamp(10px, 1.3vw, 14px)" }}>
              <span className="inline-block mt-1.5 shrink-0" style={{ width: 5, height: 5, backgroundColor: primary }} />
              <span className="leading-snug">{b}</span>
            </div>
          ))}
        </div>
        {Footer}
      </div>
      <div className="flex flex-col gap-1.5" style={{ padding: "clamp(10px, 1.6vw, 14px)" }}>
        {[0, 1, 2].map((i) => {
          const p = photos[i];
          return (
            <div key={i} className="relative overflow-hidden rounded-md flex-1"
              style={{ backgroundColor: `${primary}${i === 0 ? "1f" : i === 1 ? "16" : "10"}`, minHeight: 0 }}>
              <PhotoTile
                photo={p}
                primary={primary}
                iconSize={28}
                label={`Photo ${i + 1}`}
                sublabel="Upload in Photos"
                captionFontSize="9px"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function renderPictureOnly({ slide, primary, onUploadPhoto }) {
  const hasImage = ((slide?.images || []).length > 0);
  if (!hasImage && typeof onUploadPhoto === "function") {
    return <PictureOnlyDropzone primary={primary} onUploadPhoto={onUploadPhoto} />;
  }
  return (
    <div className="relative h-full w-full">
      <PhotoTile
        photo={(slide?.images || [])[0]}
        primary={primary}
        iconSize={44}
        label="Upload a photo"
        sublabel="This layout is just a picture — no text"
        captionFontSize="12px"
      />
    </div>
  );
}

function renderIconFeatureGrid({ bullets, primary, Header, Footer }) {
  const tiles = bullets.slice(0, 6);
  while (tiles.length < 6) tiles.push("Add a feature");
  const glyphs = ["●", "■", "▲", "◆", "★", "◐"];
  return (
    <div className="relative h-full flex flex-col" style={{ padding: "clamp(14px, 3vw, 28px)" }}>
      {Header}
      <div className="mt-3 flex-1 grid grid-cols-3 gap-2">
        {tiles.map((t, i) => (
          <div key={i} className="rounded-lg p-2 flex flex-col items-center text-center"
            style={{ backgroundColor: "#F1F5F4", border: `1px solid ${primary}22` }}>
            <div style={{ fontSize: "clamp(16px, 3vw, 26px)", color: i % 2 === 0 ? primary : "#F59E0B", lineHeight: 1 }}>
              {glyphs[i]}
            </div>
            <div className="mt-1.5 leading-tight" style={{ color: "#1F2937", fontSize: "clamp(9px, 1.05vw, 11px)", fontWeight: 600 }}>
              {t.length > 34 ? `${t.substring(0, 32)}…` : t}
            </div>
          </div>
        ))}
      </div>
      {Footer}
    </div>
  );
}

function renderNumberedStatSpotlight({ bullets, primary, Header, Footer }) {
  const stats = bullets.slice(0, 3);
  while (stats.length < 3) stats.push("Add a stat");
  const parseStat = (s) => {
    const m = s.match(/([\d$€£]+[\d,.KMBk%+]*)\s*(.*)/);
    return m ? { num: m[1], label: (m[2] || "").trim() || s } : { num: s, label: "" };
  };
  return (
    <div className="relative h-full flex flex-col" style={{ padding: "clamp(16px, 3vw, 32px)" }}>
      {Header}
      <div className="mt-3 flex-1 grid grid-cols-3 gap-3">
        {stats.map((s, i) => {
          const { num, label } = parseStat(s);
          return (
            <div key={i} className="rounded-lg flex flex-col items-center justify-center text-center p-2"
              style={{ backgroundColor: "#F1F5F4", border: `1px solid ${primary}22` }}>
              <div className="font-extrabold tracking-tight" style={{ color: primary, fontSize: "clamp(22px, 4.6vw, 44px)", letterSpacing: "-0.03em" }}>
                {num}
              </div>
              <svg viewBox="0 0 40 20" style={{ width: "60%", height: "18%", marginTop: 4 }}>
                {i === 0 && <path d="M 2 16 Q 12 4 22 12 T 38 6" stroke="#F59E0B" strokeWidth="2" fill="none" strokeLinecap="round" />}
                {i === 1 && (<g>
                  <line x1="4" y1="18" x2="4" y2="6" stroke={primary} strokeWidth="1.5" />
                  <line x1="4" y1="18" x2="36" y2="18" stroke={primary} strokeWidth="1.5" />
                  <polyline points="8,14 15,8 22,10 30,3" fill="none" stroke="#F59E0B" strokeWidth="1.6" />
                </g>)}
                {i === 2 && (<g>
                  <circle cx="14" cy="12" r="4" fill={primary} />
                  <circle cx="22" cy="12" r="4" fill="#F59E0B" />
                  <circle cx="18" cy="6" r="4" fill={primary} opacity="0.5" />
                </g>)}
              </svg>
              <div className="mt-1 leading-tight" style={{ color: "#374151", fontSize: "clamp(9px, 1.05vw, 11px)" }}>
                {label.length > 34 ? `${label.substring(0, 32)}…` : label}
              </div>
            </div>
          );
        })}
      </div>
      {Footer}
    </div>
  );
}

function renderUsVsThem({ bullets, primary, Header, Footer }) {
  const rows = bullets.slice(0, 4);
  while (rows.length < 3) rows.push("Add a feature to compare");
  return (
    <div className="relative h-full flex flex-col" style={{ padding: "clamp(16px, 3vw, 32px)" }}>
      {Header}
      <div className="mt-3 flex-1">
        <div className="grid gap-1.5" style={{ gridTemplateColumns: "1fr 60px 60px" }}>
          <div />
          <div className="text-center rounded px-1 py-0.5 font-bold text-[10px] uppercase tracking-wider" style={{ backgroundColor: primary, color: "#fff" }}>Us</div>
          <div className="text-center rounded px-1 py-0.5 font-bold text-[10px] uppercase tracking-wider" style={{ backgroundColor: "#E5E7EB", color: "#374151" }}>Them</div>
          {rows.map((r, i) => (
            <React.Fragment key={i}>
              <div className="leading-snug" style={{ color: "#1F2937", fontSize: "clamp(10px, 1.15vw, 13px)", fontWeight: 500 }}>
                {r.length > 60 ? `${r.substring(0, 58)}…` : r}
              </div>
              <div className="flex items-center justify-center">
                <div className="rounded-full w-5 h-5 grid place-items-center" style={{ backgroundColor: primary }}>
                  <span style={{ color: "#fff", fontSize: 11, fontWeight: 800 }}>✓</span>
                </div>
              </div>
              <div className="flex items-center justify-center">
                <div className="rounded-full w-5 h-5 grid place-items-center" style={{ backgroundColor: "#E5E7EB" }}>
                  <span style={{ color: "#B00020", fontSize: 11, fontWeight: 800 }}>✕</span>
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
      {Footer}
    </div>
  );
}

function renderJourneyArc({ bullets, primary, Header, Footer }) {
  const points = bullets.slice(0, 5);
  while (points.length < 4) points.push("Add a milestone");
  const coords = [[10, 78], [30, 40], [50, 58], [70, 42], [90, 26]];
  const glyphs = ["●", "■", "▲", "◆", "★"];
  return (
    <div className="relative h-full flex flex-col" style={{ padding: "clamp(16px, 3vw, 32px)" }}>
      {Header}
      <div className="mt-3 flex-1 relative">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
          <path d="M 10 78 Q 30 30, 50 58 T 90 26" stroke={primary} strokeWidth="0.8" fill="none" strokeDasharray="2 1.5" opacity="0.55" />
        </svg>
        {points.map((p, i) => {
          const [cx, cy] = coords[i] || [50, 50];
          return (
            <div key={i} className="absolute flex flex-col items-center text-center"
              style={{ left: `${cx}%`, top: `${cy}%`, transform: "translate(-50%, -50%)", width: "18%" }}>
              <div className="rounded-full grid place-items-center border-2 mb-1"
                style={{ width: 30, height: 30, borderColor: primary, backgroundColor: "#fff", color: i % 2 === 0 ? primary : "#F59E0B", fontSize: 12, fontWeight: 800 }}>
                {glyphs[i]}
              </div>
              <div className="leading-tight" style={{ color: "#1F2937", fontSize: "clamp(9px, 0.95vw, 11px)", fontWeight: 600 }}>
                {p.length > 26 ? `${p.substring(0, 24)}…` : p}
              </div>
            </div>
          );
        })}
      </div>
      {Footer}
    </div>
  );
}

function renderThanks({ deck, primary }) {
  const closing = deck.closing || {};
  const INDUSTRY_NAMES = {
    agritech: "AgriTech / Agri-business", fintech: "FinTech / Financial Services",
    healthtech: "HealthTech / Healthcare", real_estate: "Real Estate / PropTech",
    logistics: "Logistics & Supply Chain", manufacturing: "Manufacturing & FMCG",
    energy: "Clean Energy / CleanTech", education: "EdTech / Education",
    ecommerce: "Retail & E-commerce", professional_services: "Professional Services",
    saas: "SaaS / Software", other: "Other",
  };
  const tplName = INDUSTRY_NAMES[deck.industry] || "Other";
  return (
    <div className="h-full flex flex-col justify-center relative"
      style={{ background: primary, padding: "clamp(20px, 4vw, 40px)" }}>
      <div className="font-extrabold tracking-tight" style={{ color: "#fff", fontSize: "clamp(28px, 5.4vw, 60px)", letterSpacing: "-0.03em", lineHeight: 1.02 }}>
        {closing.headline || "Thank you."}
      </div>
      {closing.tagline && (
        <div className="mt-3 max-w-[80%]" style={{ color: "rgba(255,255,255,0.85)", fontSize: "clamp(13px, 1.8vw, 22px)" }}>
          {closing.tagline}
        </div>
      )}
      <div className="mt-6 space-y-1 text-[12px]" style={{ color: "rgba(255,255,255,0.88)" }}>
        {closing.email && <div>✉︎ {closing.email}</div>}
        {closing.phone && <div>☎︎ {closing.phone}</div>}
        {closing.website && <div>⌘ {closing.website}</div>}
      </div>
      <div className="absolute left-0 right-0 text-center italic"
        style={{ bottom: "clamp(8px, 1.4vw, 16px)", color: "rgba(255,255,255,0.55)", fontSize: "clamp(7px, 0.85vw, 10px)" }}
        data-testid="deck-template-footer">
        Built using the Clinq {tplName} Pitch Deck Template · CRS v2.2
      </div>
    </div>
  );
}

function renderNumberedList({ bullets, primary, Header, Footer, spacing }) {
  const items = bullets.slice(0, 5);
  return (
    <div className="relative h-full flex flex-col" style={{ padding: "clamp(16px, 3vw, 32px)" }}>
      {Header}
      <div className={`mt-3 flex-1 flex flex-col ${spacing.justify} gap-2.5 overflow-hidden`}>
        {items.map((b, i) => (
          <div key={i} className="flex items-start gap-3">
            <div className="font-extrabold font-mono-num shrink-0"
              style={{ color: primary, fontSize: "clamp(18px, 2.6vw, 28px)", lineHeight: 1, letterSpacing: "-0.02em", minWidth: 34 }}>
              {String(i + 1).padStart(2, "0")}
            </div>
            <div className="leading-snug" style={{ color: "#1F2937", fontSize: "clamp(11px, 1.4vw, 15px)", paddingTop: 4 }}>
              {b}
            </div>
          </div>
        ))}
      </div>
      {Footer}
    </div>
  );
}

function renderTwoColumnBullet({ bullets, primary, Header, Footer, spacing }) {
  const items = bullets.slice(0, 8);
  const mid = Math.ceil(items.length / 2);
  const left = items.slice(0, mid);
  const right = items.slice(mid);
  const renderCol = (col) => (
    <div className={`flex-1 flex flex-col ${spacing.justify} ${spacing.gap2}`}>
      {col.map((b, i) => (
        <div key={i} className="flex items-start gap-1.5" style={{ color: "#1F2937", fontSize: "clamp(10px, 1.25vw, 13px)" }}>
          <span className="inline-block mt-1.5 shrink-0" style={{ width: 5, height: 5, backgroundColor: primary }} />
          <span className="leading-snug">{b}</span>
        </div>
      ))}
    </div>
  );
  return (
    <div className="relative h-full flex flex-col" style={{ padding: "clamp(16px, 3vw, 32px)" }}>
      {Header}
      <div className="mt-4 flex-1 grid grid-cols-2 gap-6">
        {renderCol(left)}
        {renderCol(right)}
      </div>
      {Footer}
    </div>
  );
}

function renderQuoteCallout({ bullets, primary, Header, Footer }) {
  const items = bullets.slice(0, 4);
  return (
    <div className="relative h-full flex flex-col" style={{ padding: "clamp(16px, 3vw, 32px)" }}>
      {Header}
      <div className="mt-3 flex-1 flex flex-col justify-center gap-2">
        {items.map((b, i) => (
          <div key={i} className="rounded-md flex items-stretch overflow-hidden"
            style={{ backgroundColor: "#F1F5F4", borderLeft: `4px solid ${primary}` }}>
            <div className="px-3 py-2 flex-1" style={{ color: "#1F2937", fontSize: "clamp(11px, 1.35vw, 14px)", fontWeight: 500, lineHeight: 1.4 }}>
              {b}
            </div>
          </div>
        ))}
      </div>
      {Footer}
    </div>
  );
}

function renderDefaultBulletList({ bullets, primary, Header, Footer, spacing }) {
  return (
    <div className="relative h-full flex flex-col" style={{ padding: "clamp(16px, 3vw, 32px)" }}>
      {Header}
      <div className={`mt-4 flex-1 flex flex-col ${spacing.justify} ${spacing.gap2} overflow-hidden`}>
        {bullets.length > 0 ? bullets.slice(0, 5).map((b, i) => (
          <div key={i} className="flex items-start gap-2" style={{ color: "#1F2937", fontSize: "clamp(11px, 1.5vw, 16px)" }}>
            <span className="inline-block rounded-full mt-1.5 shrink-0" style={{ width: 6, height: 6, backgroundColor: primary }} />
            <span className="leading-snug">{b}</span>
          </div>
        )) : (
          <div className="text-sm italic" style={{ color: "#9CA3AF" }}>
            Add bullet content above to see it rendered here.
          </div>
        )}
      </div>
      {Footer}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Dispatcher — the main SlidePreviewBody delegates all non-locked
// layouts here in a single call.
// ─────────────────────────────────────────────────────────────

export function renderGalleryLayout(ctx) {
  const { layout, slide } = ctx;

  if (layout.startsWith("Full-bleed hero"))            return renderFullBleedHero(ctx);
  if (layout.startsWith("KPI dashboard"))              return renderKPIDashboard(ctx);
  if (layout.startsWith("3 pain-point cards")
      || layout.startsWith("Icon grid")
      || layout.startsWith("3-4 column team"))         return renderCardGrid(ctx);
  if (layout.startsWith("Process flow"))               return renderProcessFlow(ctx);
  if (layout.startsWith("2×2"))                        return renderCompetitiveMatrix(ctx);
  if (layout.startsWith("Timeline"))                   return renderTimeline(ctx);
  if (layout.startsWith("Funnel"))                     return renderFunnel(ctx);
  if (layout.startsWith("Illustrated hero split"))     return renderIllustratedHeroSplit(ctx);
  if (layout.startsWith("Photo left"))                 return renderPhotoLeft(ctx);
  if (layout.startsWith("Photo top"))                  return renderPhotoTop(ctx);
  if (layout.startsWith("Photo hero"))                 return renderPhotoHero(ctx);
  if (layout.startsWith("Photo grid"))                 return renderPhotoGrid(ctx);
  if (layout.startsWith("Picture only"))               return renderPictureOnly(ctx);
  if (layout.startsWith("Icon feature grid"))          return renderIconFeatureGrid(ctx);
  if (layout.startsWith("Numbered stat spotlight"))    return renderNumberedStatSpotlight(ctx);
  if (layout.startsWith("Us vs Them"))                 return renderUsVsThem(ctx);
  if (layout.startsWith("Journey arc"))                return renderJourneyArc(ctx);
  if (!layout && slide.num === 12)                     return renderThanks(ctx);
  if (layout.startsWith("Numbered vertical list"))     return renderNumberedList(ctx);
  if (layout.startsWith("Two-column bullet split"))    return renderTwoColumnBullet(ctx);
  if (layout.startsWith("Quote / callout")
      || layout.startsWith("Quote/callout")
      || layout.startsWith("Quote  callout"))          return renderQuoteCallout(ctx);
  // Default fallback — classic bullet-list
  return renderDefaultBulletList(ctx);
}
